import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import type {ExtendedItemProps, SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {type CreateMilestoneMutationCallback, ItemPickerMilestone} from '@github-ui/item-picker-milestone'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
import type React from 'react'
import {type RefObject, useCallback, useEffect, useRef, useState} from 'react'
import {fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {updateIssueMilestoneBulkByQueryRelayMutation$data} from '../mutations/__generated__/updateIssueMilestoneBulkByQueryRelayMutation.graphql'
import type {updateIssueMilestoneBulkRelayMutation$data} from '../mutations/__generated__/updateIssueMilestoneBulkRelayMutation.graphql'
import {commitCreateMilestoneMutation} from '../mutations/create-milestone-relay-mutation'
import {commitUpdateIssueMilestoneBulkByQueryMutation} from '../mutations/update-issue-milestone-bulk-by-query-relay-mutation'
import {commitUpdateIssueMilestoneBulkMutation} from '../mutations/update-issue-milestone-bulk-relay-mutation'
import type {
  ItemPickerMilestoneRelayMilestone$data,
  ItemPickerMilestoneRelayMilestone$key,
} from './__generated__/ItemPickerMilestoneRelayMilestone.graphql'
import type {ItemPickerMilestoneRelayQuery} from './__generated__/ItemPickerMilestoneRelayQuery.graphql'
import type {ItemPickerMilestoneRelayRecentlyUpdatedMilestones$key} from './__generated__/ItemPickerMilestoneRelayRecentlyUpdatedMilestones.graphql'
import type {ItemPickerMilestoneRelaySearchQuery} from './__generated__/ItemPickerMilestoneRelaySearchQuery.graphql'
//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== MilestonePicker (exported)
//                                                                              <== BulkMilestonePicker (exported)

export type MilestonePickerMilestone = Omit<ItemPickerMilestoneRelayMilestone$data, ' $fragmentType'>

export type MilestonePickerSharedMilestoneKey = ItemPickerMilestoneRelayMilestone$key

export function readMilestoneFromFragment(key: ItemPickerMilestoneRelayMilestone$key): MilestonePickerMilestone {
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData<ItemPickerMilestoneRelayMilestone$key>(ItemPickerMilestoneRelayMilestoneFragment, key)
}

export async function fetchMilestones(
  environment: Environment,
  options: {
    owner: string
    repo: string
    query: string
    count?: number
  },
): Promise<MilestonePickerMilestone[]> {
  const {owner, repo, query, count = 10} = options
  const data = await fetchQuery<ItemPickerMilestoneRelaySearchQuery>(
    environment,
    ItemPickerMilestoneRelaySearchGraphqlQuery,
    {
      owner,
      repo,
      query,
      count,
    },
  ).toPromise()

  return (data?.repository?.milestones?.nodes || []).flatMap(node => (node ? [readMilestoneFromFragment(node)] : []))
}

export type MilestonePickerTestQueries = {
  MilestonePickerQuery: ItemPickerMilestoneRelayQuery
}

export type MilestonePickerProps = {
  repo: string
  owner: string
  activeMilestone: MilestonePickerMilestone | null
  readonly?: boolean
  canCreateMilestone: boolean
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref: RefObject<HTMLButtonElement>) => React.ReactElement
  /**
   * Whether to render the milestone picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  initialFilter?: string
  shortcutHintVisible?: boolean
  noMilestoneItem?: ExtendedItemProps<MilestonePickerMilestone>
  onSelectionChanged: (selectedItems: MilestonePickerMilestone[]) => void
  showMilestoneDescription?: boolean
  title?: string
  showNoMatchItem?: boolean
}

export const ItemPickerMilestoneRelayGraphqlQuery = graphql`
  query ItemPickerMilestoneRelayQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      ...ItemPickerMilestoneRelayRecentlyUpdatedMilestones
    }
  }
`

const ItemPickerMilestoneRelayRecentlyUpdatedMilestones = graphql`
  fragment ItemPickerMilestoneRelayRecentlyUpdatedMilestones on Repository
  @inline
  @argumentDefinitions(count: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "ItemPickerMilestoneRelayRecentlyUpdatedMilestoneQuery") {
    id
    milestones(first: $count, orderBy: {field: UPDATED_AT, direction: DESC}, orderByStates: [OPEN, CLOSED]) {
      nodes {
        ...ItemPickerMilestoneRelayMilestone
      }
    }
  }
`

export const ItemPickerMilestoneRelaySearchGraphqlQuery = graphql`
  query ItemPickerMilestoneRelaySearchQuery($owner: String!, $repo: String!, $query: String, $count: Int!) {
    repository(owner: $owner, name: $repo) {
      milestones(query: $query, first: $count, orderByStates: [OPEN, CLOSED]) {
        nodes {
          ...ItemPickerMilestoneRelayMilestone
        }
      }
    }
  }
`

export const ItemPickerMilestoneRelayMilestoneFragment = graphql`
  fragment ItemPickerMilestoneRelayMilestone on Milestone @inline {
    id
    # eslint-disable-next-line relay/unused-fields
    title
    # eslint-disable-next-line relay/unused-fields
    closed
    # eslint-disable-next-line relay/unused-fields
    dueOn
    # eslint-disable-next-line relay/unused-fields
    progressPercentage
    # eslint-disable-next-line relay/unused-fields
    url
    # eslint-disable-next-line relay/unused-fields
    closedAt
  }
`

function ItemPickerFetcher({repo, owner, ...rest}: MilestonePickerProps) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<{repositoryId: string; milestones: MilestonePickerMilestone[]} | null>(null)

  useEffect(() => {
    if (!IS_SERVER) {
      clientSideRelayFetchQueryRetained<ItemPickerMilestoneRelayQuery>({
        environment,
        query: ItemPickerMilestoneRelayGraphqlQuery,
        variables: {owner, repo},
      }).subscribe({
        next: internalData => {
          // eslint-disable-next-line no-restricted-syntax
          const inlineData = readInlineData<ItemPickerMilestoneRelayRecentlyUpdatedMilestones$key>(
            ItemPickerMilestoneRelayRecentlyUpdatedMilestones,
            internalData.repository,
          )
          const fetchedMilestones =
            inlineData?.milestones?.nodes?.flatMap(milestone =>
              milestone ? [readMilestoneFromFragment(milestone)] : [],
            ) || []

          setData({
            repositoryId: inlineData?.id ?? '',
            milestones: fetchedMilestones,
          })
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      })
    }
  }, [environment, fetchKey, owner, repo])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('milestones'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const fetchMilestonesWithEnvironment = useCallback(
    (options: {owner: string; repo: string; query: string; count?: number}) => {
      return fetchMilestones(environment, options)
    },
    [environment],
  )

  const onCreate: CreateMilestoneMutationCallback = useCallback(
    (input, {onSuccess, onError}) => {
      return commitCreateMilestoneMutation({
        environment,
        input,
        onError,
        onCompleted: response => {
          onSuccess(response?.createMilestone?.milestone as MilestonePickerMilestone | null)
        },
      })
    },
    [environment],
  )

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <ItemPickerMilestone
      repo={repo}
      owner={owner}
      repositoryId={data?.repositoryId ?? ''}
      milestones={data?.milestones ?? []}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      fetchMilestones={fetchMilestonesWithEnvironment}
      onCreate={onCreate}
      {...rest}
    />
  )
}

type BulkIssueMilestonePickerProps = Omit<MilestonePickerProps, 'issueId' | 'onIssueUpdate' | 'onSelectionChanged'> &
  SharedBulkActionsItemPickerProps

export function BulkIssueMilestonePicker({
  onError,
  onCompleted,
  issuesToActOn,
  useQueryForAction,
  repositoryId,
  query,
  ...rest
}: BulkIssueMilestonePickerProps) {
  const environment = useRelayEnvironment()
  const onSelectionChange = useCallback(
    (selectedMilestone: MilestonePickerMilestone[]) => {
      const milestoneId =
        selectedMilestone.length === 1 && selectedMilestone[0]?.id !== 'no-milestone' ? selectedMilestone[0]?.id : null
      const clearMilestone = selectedMilestone[0]?.id === 'no-milestone'
      if (!milestoneId && !clearMilestone) return
      const mutationArgs = {
        environment,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueMilestoneBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            milestoneId: milestoneId || undefined,
            clearMilestone: clearMilestone || undefined,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueMilestoneBulkByQueryRelayMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueMilestoneBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            milestoneId: milestoneId || undefined,
            clearMilestone: clearMilestone || undefined,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueMilestoneBulkRelayMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [environment, issuesToActOn, onCompleted, onError, query, repositoryId, useQueryForAction],
  )

  return <MilestonePicker onSelectionChanged={onSelectionChange} {...rest} />
}

export function MilestonePicker({
  repo,
  owner,
  shortcutEnabled,
  anchorElement,
  readonly,
  activeMilestone,
  noMilestoneItem,
  ...rest
}: MilestonePickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-milestone"
      anchorElement={(ref, anchorProps) => anchorElement(ref, anchorProps)}
      createChild={() => (
        <ItemPickerFetcher
          repo={repo}
          owner={owner}
          readonly={readonly}
          shortcutEnabled={shortcutEnabled}
          initialFilter={''}
          activeMilestone={activeMilestone}
          triggerOpen
          anchorElement={anchorElement}
          noMilestoneItem={noMilestoneItem}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}

export {ItemPickerMilestoneRelayGraphqlQuery as MilestonesPickerGraphqlQuery}

try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ BulkIssueMilestonePicker.displayName ||= 'BulkIssueMilestonePicker' } catch {}
try{ MilestonePicker.displayName ||= 'MilestonePicker' } catch {}